import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { STOCK_LEVELS } from '../../config/constants';
import { getPrefixedStorageName } from '../../utils/storagePrefix';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface ProductsTablePreferences {
    dense: boolean;
    rowsPerPage: number;
    sortOrder: 'asc' | 'desc';
    sortBy: string;
    filters: {
        stockStatus: 'all' | 'in-stock' | 'low-stock' | 'out-of-stock';
        lowStockThreshold: number;
        priceMin: number | null;
        priceMax: number | null;
        discountMin: number | null;
        discountMax: number | null;
        categories: string[];
        variantStatus: 'all' | 'with-variants' | 'no-variants';
        activeStatus: 'all' | 'active' | 'inactive';
    };
}

export interface OrdersTablePreferences {
    dense: boolean;
    rowsPerPage: number;
    sortOrder: 'asc' | 'desc';
    sortBy: string;
    deliveryFilter: 'all' | 'sameday' | 'standard';
}

export interface UsersTablePreferences {
    dense: boolean;
    rowsPerPage: number;
    sortOrder: 'asc' | 'desc';
    sortBy: string;
}

export interface AuditTablePreferences {
    dense: boolean;
    rowsPerPage: number;
    sortOrder: 'asc' | 'desc';
    sortBy: string;
    filters: {
        activityTypes: string[];
        deviceTypes: string[];
        userType: 'all' | 'authenticated' | 'anonymous';
        dateRange: 'all' | '1h' | '6h' | '24h' | '7d' | '30d';
        screens: string[];
    };
}

export interface RolesTablePreferences {
    dense: boolean;
}

export interface AnalyticsPreferences {
    activeTimeFilter: string;
}

// ============================================================================
// DEFAULT PREFERENCES
// ============================================================================

const defaultProductsPreferences: ProductsTablePreferences = {
    dense: true,
    rowsPerPage: 8,
    sortOrder: 'desc',
    sortBy: 'updated',
    filters: {
        stockStatus: 'all',
        lowStockThreshold: STOCK_LEVELS.MEDIUM_THRESHOLD,
        priceMin: null,
        priceMax: null,
        discountMin: null,
        discountMax: null,
        categories: [],
        variantStatus: 'all',
        activeStatus: 'all'
    }
};

const defaultOrdersPreferences: OrdersTablePreferences = {
    dense: false,
    rowsPerPage: 10,
    sortOrder: 'desc',
    sortBy: 'created',
    deliveryFilter: 'all'
};

const defaultUsersPreferences: UsersTablePreferences = {
    dense: false,
    rowsPerPage: 10,
    sortOrder: 'desc',
    sortBy: 'created'
};

const defaultAuditPreferences: AuditTablePreferences = {
    dense: true,
    rowsPerPage: 10,
    sortOrder: 'desc',
    sortBy: 'created',
    filters: {
        activityTypes: [],
        deviceTypes: [],
        userType: 'all',
        dateRange: 'all',
        screens: []
    }
};

const defaultRolesPreferences: RolesTablePreferences = {
    dense: false
};

const defaultAnalyticsPreferences: AnalyticsPreferences = {
    activeTimeFilter: '24h'
};

// ============================================================================
// STORE INTERFACE
// ============================================================================

interface TablePreferencesStore {
    // State
    products: ProductsTablePreferences;
    orders: OrdersTablePreferences;
    users: UsersTablePreferences;
    audit: AuditTablePreferences;
    roles: RolesTablePreferences;
    analytics: AnalyticsPreferences;

    // Products Table Actions
    updateProductsPreferences: (preferences: Partial<ProductsTablePreferences>) => void;
    updateProductsFilters: (filters: Partial<ProductsTablePreferences['filters']>) => void;
    resetProductsPreferences: () => void;

    // Orders Table Actions
    updateOrdersPreferences: (preferences: Partial<OrdersTablePreferences>) => void;
    resetOrdersPreferences: () => void;

    // Users Table Actions
    updateUsersPreferences: (preferences: Partial<UsersTablePreferences>) => void;
    resetUsersPreferences: () => void;

    // Audit Table Actions
    updateAuditPreferences: (preferences: Partial<AuditTablePreferences>) => void;
    updateAuditFilters: (filters: Partial<AuditTablePreferences['filters']>) => void;
    resetAuditPreferences: () => void;

    // Roles Table Actions
    updateRolesPreferences: (preferences: Partial<RolesTablePreferences>) => void;
    resetRolesPreferences: () => void;

    // Analytics Actions
    updateAnalyticsPreferences: (preferences: Partial<AnalyticsPreferences>) => void;
    resetAnalyticsPreferences: () => void;

    // Global Actions
    resetAllPreferences: () => void;
}

// ============================================================================
// STORE IMPLEMENTATION
// ============================================================================

export const tablePreferencesStore = create(
    persist<TablePreferencesStore>(
        (set) => ({
            // Initial State
            products: defaultProductsPreferences,
            orders: defaultOrdersPreferences,
            users: defaultUsersPreferences,
            audit: defaultAuditPreferences,
            roles: defaultRolesPreferences,
            analytics: defaultAnalyticsPreferences,

            // Products Table Actions
            updateProductsPreferences: (preferences) =>
                set((state) => ({
                    products: { ...state.products, ...preferences }
                })),

            updateProductsFilters: (filters) =>
                set((state) => ({
                    products: {
                        ...state.products,
                        filters: { ...state.products.filters, ...filters }
                    }
                })),

            resetProductsPreferences: () =>
                set(() => ({
                    products: defaultProductsPreferences
                })),

            // Orders Table Actions
            updateOrdersPreferences: (preferences) =>
                set((state) => ({
                    orders: { ...state.orders, ...preferences }
                })),

            resetOrdersPreferences: () =>
                set(() => ({
                    orders: defaultOrdersPreferences
                })),

            // Users Table Actions
            updateUsersPreferences: (preferences) =>
                set((state) => ({
                    users: { ...state.users, ...preferences }
                })),

            resetUsersPreferences: () =>
                set(() => ({
                    users: defaultUsersPreferences
                })),

            // Audit Table Actions
            updateAuditPreferences: (preferences) =>
                set((state) => ({
                    audit: { ...state.audit, ...preferences }
                })),

            updateAuditFilters: (filters) =>
                set((state) => ({
                    audit: {
                        ...state.audit,
                        filters: { ...state.audit.filters, ...filters }
                    }
                })),

            resetAuditPreferences: () =>
                set(() => ({
                    audit: defaultAuditPreferences
                })),

            // Roles Table Actions
            updateRolesPreferences: (preferences) =>
                set((state) => ({
                    roles: { ...state.roles, ...preferences }
                })),

            resetRolesPreferences: () =>
                set(() => ({
                    roles: defaultRolesPreferences
                })),

            // Analytics Actions
            updateAnalyticsPreferences: (preferences) =>
                set((state) => ({
                    analytics: { ...state.analytics, ...preferences }
                })),

            resetAnalyticsPreferences: () =>
                set(() => ({
                    analytics: defaultAnalyticsPreferences
                })),

            // Global Actions
            resetAllPreferences: () =>
                set(() => ({
                    products: defaultProductsPreferences,
                    orders: defaultOrdersPreferences,
                    users: defaultUsersPreferences,
                    audit: defaultAuditPreferences,
                    roles: defaultRolesPreferences,
                    analytics: defaultAnalyticsPreferences
                }))
        }),
        {
            name: getPrefixedStorageName('table-preferences-storage'),
            storage: createJSONStorage(() => AsyncStorage),
        }
    )
);

